import { api } from './client';
import type { LegalEntity } from '@shared/types';

export interface ReconciliationOperation {
  date: string;
  description: string;
  debit: number;
  credit: number;
  invoiceId?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  actId?: string;  // ID акта (для скачивания PDF акта)
  actDate?: string;
  actReturnStatus?: string;
  paymentId?: string;
  documentNumber?: string;
  isPending?: boolean;
}

export interface ReconciliationData {
  client?: {
    byActs?: boolean;
  };
  operations: ReconciliationOperation[];
  openingBalance: { debit: number; credit: number };
  turnover: { debit: number; credit: number };
  closingBalance: { debit: number; credit: number };
}

export interface ReconciliationParams {
  clientId: number;
  legalEntity: LegalEntity;
  dateFrom: string;
  dateTo: string;
  signerId?: string;
}

export interface Signer {
  id: string;
  legalEntity: LegalEntity;
  shortName: string;
  position: string;
  priority: number;
}

export interface UnpaidInvoice {
  id: string;
  number: number;
  date: string;
  amount: number;
  paymentState: string;
}

export interface InvoiceDetail {
  pcCount: number;
  serverCount: number;
  pricePerPc: number;
  pricePerServer: number;
  pcTotal: number;
  serverTotal: number;
  servicesTotal: number;
  amount: number;
  vatRate: number;
  accountedPCs: number;
  employee: { firstName: string; lastName: string } | null;
  offices: Array<{
    office: { name: string } | null;
    employee: { firstName: string; lastName: string } | null;
    accountedPCs: number;
  }>;
  services: Array<{
    name: string;
    total: number;
  }>;
}

export const reconciliationApi = {
  getData: async (params: ReconciliationParams): Promise<ReconciliationData> => {
    const { data } = await api.get('/reconciliation', { params });
    return data;
  },

  getHtml: async (params: ReconciliationParams): Promise<string> => {
    const { data } = await api.get('/reconciliation/html', {
      params,
      responseType: 'text',
    });
    return data;
  },

  getPdf: async (params: ReconciliationParams): Promise<Blob> => {
    const { data } = await api.get('/reconciliation/pdf', {
      params,
      responseType: 'blob',
    });
    return data;
  },

  getPdfAsBase64: async (params: ReconciliationParams): Promise<string> => {
    const blob = await reconciliationApi.getPdf(params);
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  },

  getAvailableSigners: async (legalEntity: LegalEntity, date: string): Promise<Signer[]> => {
    const { data } = await api.get('/requisite-signers/available', {
      params: { legalEntity, date },
    });
    return data;
  },

  getUnpaidInvoices: async (clientId: number, legalEntity: LegalEntity): Promise<UnpaidInvoice[]> => {
    const { data } = await api.get('/invoices', {
      params: {
        clientId,
        legalEntity,
        paymentState: 'UNPAID',
        limit: 100,
        sortBy: 'date',
        sortOrder: 'desc',
      },
    });
    return data.items || [];
  },

  // PDF для счетов (isPending = true)
  getInvoicePdf: async (invoiceId: string, signerId?: string): Promise<Blob> => {
    const { data } = await api.get(`/invoices/${invoiceId}/pdf`, {
      params: signerId ? { signerId } : {},
      responseType: 'blob',
    });
    return data;
  },

  getInvoicePdfAsBase64: async (invoiceId: string, signerId?: string): Promise<string> => {
    const blob = await reconciliationApi.getInvoicePdf(invoiceId, signerId);
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  },

  // PDF для актов (isPending = false)
  getActPdf: async (actId: string, signerId?: string): Promise<Blob> => {
    const { data } = await api.get(`/acts/${actId}/pdf`, {
      params: signerId ? { signerId } : {},
      responseType: 'blob',
    });
    return data;
  },

  getActPdfAsBase64: async (actId: string, signerId?: string): Promise<string> => {
    const blob = await reconciliationApi.getActPdf(actId, signerId);
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  },

  // Универсальный метод - выбирает invoice или act в зависимости от isPending
  // isPending=true: счёт без акта -> использовать invoiceId для /invoices/:id/pdf
  // isPending=false: акт -> использовать actId для /acts/:id/pdf
  getDocumentPdfAsBase64: async (
    invoiceId: string,
    actId: string | undefined,
    isPending: boolean,
    signerId?: string
  ): Promise<string> => {
    if (isPending) {
      // Счёт без акта - используем invoiceId
      return reconciliationApi.getInvoicePdfAsBase64(invoiceId, signerId);
    } else {
      // Акт - используем actId
      if (!actId) {
        throw new Error('actId is required for act PDF');
      }
      return reconciliationApi.getActPdfAsBase64(actId, signerId);
    }
  },

  getInvoiceDetail: async (invoiceId: string): Promise<InvoiceDetail> => {
    const { data } = await api.get(`/invoices/${invoiceId}`);
    return data;
  },
};
